import { api } from '@/lib/api';
import type {
  AudienceResponse,
  AudienceEntryResponse,
  CreateAudienceRequest,
  UpdateAudienceRequest,
  CreateAudienceEntryRequest,
  UpdateAudienceEntryRequest,
  AudienceQuery,
  AudienceEntryQuery,
  AudienceImportPreviewResponse,
  AudienceImportResponse,
  AudienceDropdownItem,
  AudienceIdentifierType,
} from '@troia-v3/types';

export interface AudiencesListResponse {
  items: AudienceResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AudienceEntriesListResponse {
  items: AudienceEntryResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Audiences Service
 * CRUD + entries + CSV import + dropdown
 */
export const audiencesService = {
  // ============================================================================
  // Audiences CRUD
  // ============================================================================

  async getAll(query?: AudienceQuery): Promise<AudiencesListResponse> {
    const response = await api.get('/audiences', { params: query });
    return response.data.data;
  },

  async getById(id: string): Promise<AudienceResponse> {
    const response = await api.get(`/audiences/${id}`);
    return response.data.data;
  },

  async create(data: CreateAudienceRequest): Promise<AudienceResponse> {
    const response = await api.post('/audiences', data);
    return response.data.data;
  },

  async update(id: string, data: UpdateAudienceRequest): Promise<AudienceResponse> {
    const response = await api.put(`/audiences/${id}`, data);
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/audiences/${id}`);
  },

  // ============================================================================
  // Audience Entries CRUD
  // ============================================================================

  async getEntries(
    audienceId: string,
    query?: AudienceEntryQuery
  ): Promise<AudienceEntriesListResponse> {
    const response = await api.get(`/audiences/${audienceId}/entries`, { params: query });
    return response.data.data;
  },

  async createEntry(
    audienceId: string,
    data: CreateAudienceEntryRequest
  ): Promise<AudienceEntryResponse> {
    const response = await api.post(`/audiences/${audienceId}/entries`, data);
    return response.data.data;
  },

  async updateEntry(
    audienceId: string,
    entryId: string,
    data: UpdateAudienceEntryRequest
  ): Promise<AudienceEntryResponse> {
    const response = await api.put(`/audiences/${audienceId}/entries/${entryId}`, data);
    return response.data.data;
  },

  async deleteEntry(audienceId: string, entryId: string): Promise<void> {
    await api.delete(`/audiences/${audienceId}/entries/${entryId}`);
  },

  // ============================================================================
  // CSV Import
  // ============================================================================

  async importPreview(audienceId: string, file: File): Promise<AudienceImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/audiences/${audienceId}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  async startImport(
    audienceId: string,
    file: File,
    identifierColumn: string,
    nameColumn?: string
  ): Promise<AudienceImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('identifierColumn', identifierColumn);
    if (nameColumn) {
      formData.append('nameColumn', nameColumn);
    }
    const response = await api.post(`/audiences/${audienceId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  // ============================================================================
  // Dropdown (cross-module — no permission middleware on backend)
  // ============================================================================

  async getForDropdown(identifierType?: AudienceIdentifierType): Promise<AudienceDropdownItem[]> {
    const params = identifierType ? { identifierType } : {};
    const response = await api.get('/audiences/dropdown', { params });
    return response.data.data;
  },

  async getDropdownById(id: string): Promise<AudienceDropdownItem> {
    const response = await api.get(`/audiences/dropdown/${id}`);
    return response.data.data;
  },
};
